import { assertNotEmpty, type Optional } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { RawSelect } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useEffect, useState } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import type { TemplateReferenceValueSetupField } from '../../types/templateModel'
import {
  generateEmptyTemplateValueSetup,
  generateEmptyTemplateVariant,
} from '../../uiModels/createEmptyFieldHelpers'
import { useUnusedTemplateReferences } from './useUnusedTemplateReferences'

type Props = {
  field: TemplateReferenceValueSetupField
}

/**
 * Row used for adding a new template reference to template value setup field.
 * The component is loaded in edit mode by the details panel to load the list of
 * available templates and allow the user to add new template references to the value
 * setup.
 */
export const TemplateReferenceAddVariantRow = ({ field }: Props) => {
  const { templateVersion } = useTemplateContext()
  const translate = useTranslate()
  const availableTemplates = useUnusedTemplateReferences(field)
  const [currentTemplateReferenceId, setCurrentTemplateReferenceId] = useState<
    Optional<string>
  >(availableTemplates[0])

  assertNotEmpty(templateVersion)

  useEffect(() => {
    if (
      currentTemplateReferenceId &&
      !availableTemplates.includes(currentTemplateReferenceId)
    ) {
      setCurrentTemplateReferenceId(availableTemplates[0])
    }
  }, [availableTemplates, currentTemplateReferenceId])

  const handleValueChanged = (nextValue: Optional<string>) => {
    setCurrentTemplateReferenceId(nextValue)
  }

  const handleAddVariant = async () => {
    if (currentTemplateReferenceId) {
      const fieldValue = field.value ?? generateEmptyTemplateValueSetup(field)
      assertNotEmpty(templateVersion)

      const nextVariant = generateEmptyTemplateVariant({
        source: currentTemplateReferenceId,
        fieldValue,
      })

      await field.update({
        ...fieldValue,
        origin: fieldValue.path,
        variants: [nextVariant, ...fieldValue.variants],
      })
    }
  }

  return (
    <div className="flex flex-row items-center gap-x-2">
      <RawSelect
        options={availableTemplates}
        onChange={handleValueChanged}
        value={currentTemplateReferenceId}
      />
      <Button
        onClick={handleAddVariant}
        disabled={!currentTemplateReferenceId}
        startIcon="PlusCircle"
      >
        {translate('add')}
      </Button>
    </div>
  )
}
